import { column, Css, GridColumn, GridDataRow, GridTable, numericColumn, simpleHeader } from "@homebound/beam";
import { priceCell } from "src/components/gridTableCells";
import { PlanBasedLineItemsTab_LineItemFragment } from "src/routes/projects/commitments/components/PlanBasedLineItemsTab.generated";

type PlanBasedBidLinesSectionProps = { lineItems: PlanBasedLineItemsTab_LineItemFragment[] };

export function PlanBasedBidLinesSection({ lineItems }: PlanBasedBidLinesSectionProps) {
  return (
    <section data-testid="planBasedBidLinesSection">
      <h2 css={Css.mdSb.mt3.mb1.$}>Cost Lines</h2>
      <GridTable columns={columns} rows={createRows(lineItems)} />
    </section>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = {
  kind: "data";
  data: {
    option: string;
    dimensions: string;
    committedInCents: number;
    billedInCents: number;
    unbilledInCents: number;
    paidInCents: number;
    balanceInCents: number;
  };
};
type Row = HeaderRow | DataRow;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: () => "Option", data: ({ option }) => option }),
  column<Row>({ header: () => "Dimensions", data: ({ dimensions }) => dimensions }),
  numericColumn<Row>({
    header: () => "Committed",
    data: ({ committedInCents }) => priceCell({ id: "committed", valueInCents: committedInCents }),
  }),
  numericColumn<Row>({
    header: { content: "Billed", tooltip: "Approved bills" },
    data: ({ billedInCents }) => priceCell({ id: "billed", valueInCents: billedInCents }),
  }),
  numericColumn<Row>({
    header: () => "Unbilled",
    data: ({ unbilledInCents }) => priceCell({ id: "unbilled", valueInCents: unbilledInCents }),
  }),
  numericColumn<Row>({
    header: { content: "Paid to Date", tooltip: "Amount of cash paid against billed" },
    data: ({ paidInCents }) => priceCell({ id: "paid", valueInCents: paidInCents }),
  }),
  numericColumn<Row>({
    header: { content: "Pending", tooltip: "Approved bills that are not yet paid" },
    data: ({ balanceInCents }) => priceCell({ id: "pending", valueInCents: balanceInCents }),
  }),
];

function createRows(lineItems: PlanBasedLineItemsTab_LineItemFragment[]): GridDataRow<Row>[] {
  const dataRows = lineItems
    .filter((cli) => cli.planBasedLineItem)
    .groupByObject((cli) => cli.planBasedLineItem!)
    .map(([pbbcli, clis]) => {
      const { groupLine } = pbbcli;
      return {
        kind: "data" as const,
        id: pbbcli.id,
        data: {
          option: groupLine.group.displayName,
          dimensions: groupLine.options.map((o) => o.name).join(" "),
          committedInCents: clis.sum((cli) => cli.committedInCents),
          billedInCents: clis.sum((cli) => cli.billedInCents ?? 0),
          unbilledInCents: clis.sum((cli) => cli.unbilledInCents),
          paidInCents: clis.sum((cli) => cli.paidInCents ?? 0),
          balanceInCents: clis.sum((cli) => cli.balanceInCents ?? 0),
        },
      };
    });
  return [simpleHeader, ...dataRows];
}
